import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { foutmelding } from '../api/fouten.ts';

interface Props<T> {
  query: UseQueryResult<T>;
  leeg?: ReactNode | undefined;
  isLeeg?: ((data: T) => boolean) | undefined;
  children: (data: T) => ReactNode;
}

/**
 * Toont een query: een spinner tijdens het laden, een foutmelding met "Opnieuw", een lege staat of de inhoud.
 * @param props De query, de tekst bij een lege lijst en wat er met de data getoond wordt.
 * @returns De juiste staat.
 */
export function Laadstatus<T>({ query, leeg, isLeeg, children }: Props<T>) {
  if (query.isPending) {
    return <Laden />;
  }
  if (query.isError) {
    return <Foutmelding fout={query.error} opnieuw={() => void query.refetch()} />;
  }
  const data = query.data;
  const controleerLeeg = isLeeg ?? ((waarde: T) => Array.isArray(waarde) && waarde.length === 0);
  if (leeg !== undefined && controleerLeeg(data)) {
    return <LegeStaat>{leeg}</LegeStaat>;
  }
  return <>{children(data)}</>;
}

/**
 * Een spinner in het midden van een vlak.
 * @param props De minimale hoogte van het vlak.
 * @returns De spinner.
 */
export function Laden({ hoogte = 160 }: { hoogte?: number | undefined }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: hoogte }}>
      <CircularProgress size={28} />
    </Box>
  );
}

/**
 * Een foutmelding met eventueel een knop om het opnieuw te proberen.
 * @param props De fout en de actie om opnieuw te laden.
 * @returns De melding.
 */
export function Foutmelding({ fout, opnieuw }: { fout: unknown; opnieuw?: (() => void) | undefined }) {
  return (
    <Alert
      severity="error"
      action={
        opnieuw ? (
          <Button color="inherit" size="small" onClick={opnieuw}>
            Opnieuw
          </Button>
        ) : undefined
      }
    >
      {foutmelding(fout)}
    </Alert>
  );
}

/**
 * De tekst bij een lege lijst.
 * @param props De tekst.
 * @returns De lege staat.
 */
export function LegeStaat({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">{children}</Typography>
    </Box>
  );
}
